import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Toolbar,
  AppBar,
  Badge,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Pagination,
  Grid,
  Divider,
  styled,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import LeftToolbar from "./LeftToolbar";
import Footer from "./Footer";
import NotificationSnackbar from "./NotificationBar";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FilterIcon from "@mui/icons-material/FilterList";
import CalendarToday from "@mui/icons-material/CalendarToday";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
  overflowX: "auto",
  background: "white",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: alpha(theme.palette.primary.light, 0.03),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.08),
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[1],
  },
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&:first-of-type": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const StatusBadge = styled("span")(({ status, theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 12px",
  borderRadius: "16px",
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "capitalize",
  backgroundColor:
    status === "New"
      ? alpha(theme.palette.success.light, 0.2)
      : status === "Assigned"
      ? alpha(theme.palette.warning.light, 0.2)
      : status === "In Progress"
      ? alpha(theme.palette.info.light, 0.2)
      : alpha(theme.palette.grey[300], 0.2),
  color:
    status === "New"
      ? theme.palette.success.dark
      : status === "Assigned"
      ? theme.palette.warning.dark
      : status === "In Progress"
      ? theme.palette.info.dark
      : theme.palette.grey[800],
  "&:before": {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: "50%",
    marginRight: 6,
    backgroundColor:
      status === "New"
        ? theme.palette.success.main
        : status === "Assigned"
        ? theme.palette.warning.main
        : status === "In Progress"
        ? theme.palette.info.main
        : theme.palette.grey[500],
  },
}));

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    
    const pad = (num) => num.toString().padStart(2, "0");
    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )} ${pad(hours)}:${pad(date.getMinutes())} ${ampm}`;
  } catch {
    return "N/A";
  }
};

const AssignedTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ticketsPerPage = 5;
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    mode: "none",
    singleDate: "",
    startDate: "",
    endDate: "",
  });
  const [roleFilter, setRoleFilter] = useState("");
    const [userFilter, setUserFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "AssignedOn",
    direction: "asc",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedMenu, setSelectedMenu] = useState("Assigned Tickets");
  const [user] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : {};
    } catch (error) {
      console.error("Error parsing user data:", error);
      return {};
    }
  });

  const navigate = useNavigate();

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/assignedTickets", {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      setTickets(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setLoading(false);
      setTickets([]);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const highlightMatch = (text, term) => {
    if (!term || !text) return text || '';
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTerm})`, "gi");
    return String(text)
      .split(regex)
      .map((part, i) =>
        regex.test(part) ? <mark key={i}>{part}</mark> : part
      );
  };

  const sortTickets = (ticketsToSort) => {
    if (!sortConfig.key || !ticketsToSort) return ticketsToSort || [];
    return [...ticketsToSort].sort((a, b) => {
      if (sortConfig.key === "AssignedOn" || sortConfig.key === "CreatedOn") {
        const dateA = new Date(a?.[sortConfig.key] || 0).getTime();
        const dateB = new Date(b?.[sortConfig.key] || 0).getTime();
        if (dateA < dateB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (dateA > dateB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      }
      if ((a?.[sortConfig.key] || "") < (b?.[sortConfig.key] || "")) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if ((a?.[sortConfig.key] || "") > (b?.[sortConfig.key] || "")) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const filteredTickets = sortTickets(
    (tickets || []).filter((ticket) => {
      const matchesSearch =
        (ticket?.TicketDescription?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) ||
        (ticket?.Sender?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (ticket?.InstitutionName?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        );

      const matchesCategory =
        selectedCategory === "" || ticket?.categoryName === selectedCategory;

      let matchesDate = true;
      if (dateFilter.mode === "single" && dateFilter.singleDate) {
        const ticketDate = new Date(ticket?.AssignedOn || 0)
          .toISOString()
          .split("T")[0];
        matchesDate = ticketDate === dateFilter.singleDate;
      } else if (dateFilter.mode === "range") {
        const ticketDate = new Date(ticket?.AssignedOn || 0).getTime();
        const startDate = dateFilter.startDate
          ? new Date(dateFilter.startDate).getTime()
          : 0;
        const endDate = dateFilter.endDate
          ? new Date(dateFilter.endDate).getTime() + 86400000
          : Infinity;
        matchesDate = ticketDate >= startDate && ticketDate < endDate;
      }

      const matchesRole =
        roleFilter === "" || ticket?.UserRoleId === roleFilter;
    const matchesUser =
        userFilter === "" || ticket?.AssignedTo === userFilter;

      return matchesSearch && matchesCategory && matchesDate && matchesRole && matchesUser;
    })
  );

  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = filteredTickets?.slice(indexOfFirstTicket, indexOfLastTicket) || [];

  const uniqueCategories = [
    ...new Set(tickets?.map((ticket) => ticket?.categoryName).filter(Boolean) || [])
  ];

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleOpenDialog = (ticket) => {
    setSelectedTicket(ticket || null);
    setResponseText("");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    const userToken = localStorage.getItem("userToken");
    if (!userToken) {
      handleSnackbarOpen("Unauthorized. Please login first.", "error");
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("ticketId", selectedTicket?.id);
    formDataToSend.append("respondedBy", user?.id);
    formDataToSend.append("responseMessage", responseText);
    try {
      const res = await fetch("/api/support/respond-ticket", {
        method: "POST",
        credentials: "include",
        body: formDataToSend,
        headers: {
          Authorization: `${userToken}`,
        },
      });
      const data = await res.json();
      if (!user?.id) {
        handleSnackbarOpen("No user found", "error");
        return;
      }
      if (!res.ok) {
        throw new Error(data.message || "Failed to submit response.");
      }

      handleSnackbarOpen(
        data.message || "Response submitted successfully!",
        "success"
      );
      await fetchTickets();
      setOpenDialog(false);
    } catch (error) {
      console.error("Submit response error:", error);
      const message = error?.message || "An error occurred!";
      handleSnackbarOpen(message, "error");
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (
      dateFilter.mode === "range" &&
      dateFilter.startDate &&
      dateFilter.endDate
    ) {
      const start = new Date(dateFilter.startDate);
      const end = new Date(dateFilter.endDate);
      if (start > end) {
        handleSnackbarOpen("End date cannot be before start date", "warning");
      }
    }
  }, [dateFilter.mode, dateFilter.startDate, dateFilter.endDate]);


  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow:
            "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            IPPIS SUPPORT PORTAL
          </Typography>

          <IconButton color="inherit">
            <Badge
              badgeContent={
                filteredTickets?.length > 9999 ? "9999+" : filteredTickets?.length || 0
              }
              color="error"
              max={99999}
              onClick={() => navigate("/Dashboard")}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
        onCollapse={setCollapsed}
      />

      <MainContent $collapsed={collapsed}>
        <Toolbar />

        {/* Active filters display */}
        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {selectedCategory && (
            <Chip
              label={`Module: ${selectedCategory}`}
              onDelete={() => setSelectedCategory("")}
              sx={{ borderRadius: "12px" }}
            />
          )}

          {roleFilter && (
            <Chip
              label={`Role: ${roleFilter}`}
              onDelete={() => setRoleFilter("")}
              sx={{ borderRadius: "12px" }}
            />
          )}


                 {userFilter && (
            <Chip
              label={`User: ${userFilter}`}
              onDelete={() => setUserFilter("")}
              sx={{ borderRadius: "12px" }}
            />
          )}

          {dateFilter.mode === "single" && dateFilter.singleDate && (
            <Chip
              label={`Date: ${new Date(
                dateFilter.singleDate
              ).toLocaleDateString()}`}
              onDelete={() => setDateFilter({ ...dateFilter, singleDate: "" })}
              sx={{ borderRadius: "12px" }}
            />
          )}
          {dateFilter.mode === "range" &&
            (dateFilter.startDate || dateFilter.endDate) && (
              <Chip
                label={`Range: ${
                  dateFilter.startDate
                    ? new Date(dateFilter.startDate).toLocaleDateString()
                    : "..."
                } to ${
                  dateFilter.endDate
                    ? new Date(dateFilter.endDate).toLocaleDateString()
                    : "..."
                }`}
                onDelete={() =>
                  setDateFilter({ mode: "range", startDate: "", endDate: "" })
                }
                sx={{ borderRadius: "12px" }}
              />
            )}
        </Box>

        {/* Search and filter controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <TextField
            label="Search tickets..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{ display: "flex", gap: 2, width: isMobile ? "100%" : "auto" }}
          >
            {/* Date filter controls */}
            <Select
              value={dateFilter.mode}
              onChange={(e) =>
                setDateFilter({ ...dateFilter, mode: e.target.value })
              }
              size="small"
              sx={{ minWidth: 120, borderRadius: "12px" }}
            >
              <MenuItem value="none">No date filter</MenuItem>
              <MenuItem value="single">Specific date</MenuItem>
              <MenuItem value="range">Date range</MenuItem>
            </Select>

            {dateFilter.mode === "single" && (
              <TextField
                type="date"
                size="small"
                value={dateFilter.singleDate}
                onChange={(e) =>
                  setDateFilter({ ...dateFilter, singleDate: e.target.value })
                }
                sx={{ borderRadius: "12px" }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {dateFilter.mode === "range" && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={dateFilter.startDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, startDate: e.target.value })
                  }
                  sx={{ borderRadius: "12px" }}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography sx={{ alignSelf: "center" }}>to</Typography>
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={dateFilter.endDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, endDate: e.target.value })
                  }
                  sx={{ borderRadius: "12px" }}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            {/* Quick date range presets */}
            {dateFilter.mode === "range" && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);

                    setDateFilter({
                      ...dateFilter,
                      startDate: yesterday.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Yesterday
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const lastWeek = new Date(today);
                    lastWeek.setDate(lastWeek.getDate() - 7);

                    setDateFilter({
                      ...dateFilter,
                      startDate: lastWeek.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Last 7 days
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const lastMonth = new Date(today);
                    lastMonth.setMonth(lastMonth.getMonth() - 1);

                    setDateFilter({
                      ...dateFilter,
                      startDate: lastMonth.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Last 30 days
                </Button>
              </Box>
            )}

            {/* Clear date filters button */}
            {(dateFilter.mode === "single" || dateFilter.mode === "range") && (
              <Button
                variant="outlined"
                onClick={() =>
                  setDateFilter({
                    mode: "none",
                    singleDate: "",
                    startDate: "",
                    endDate: "",
                  })
                }
                sx={{ borderRadius: "12px" }}
              >
                Clear
              </Button>
            )}

            {/* Role filter */}
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="">All Roles</MenuItem>
              {[...new Set(tickets?.map((ticket) => ticket?.UserRoleId).filter(Boolean))].map(
                (role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                )
              )}
            </Select>
            {/* Role filter */}
            <Select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="">All Users</MenuItem>
              {[...new Set(tickets?.map((ticket) => ticket?.AssignedTo).filter(Boolean))].map(
                (user) => (
                  <MenuItem key={user} value={user}>
                    {user}
                  </MenuItem>
                )
              )}
            </Select>

            {/* Category filter */}
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 200,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="">All Modules</MenuItem>
              {uniqueCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Tickets table */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 3,
            mb: 3,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Assigned Tickets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredTickets?.length || 0}{" "}
              {filteredTickets?.length === 1 ? "ticket" : "tickets"} found
            </Typography>
          </Box>

          <ResponsiveTableContainer component={Paper}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: theme.palette.primary.light,
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    },
                  }}
                >
                  {[
                    { id: "id", label: "Id" },
                    { id: "TicketDescription", label: "Description" },
                    { id: "Sender", label: "Sender" },
                    { id: "categoryName", label: "Module" },
                    { id: "UserRoleId", label: "Role" },
                    { id: "AssignedOn", label: "Assigned Date" },
                    { id: "assignedFrom", label: "Assigned By" },
                    { id: "assignReason", label: "Reason" },
                    { id: "AssignedTo", label: "Assigned To" },
                    { id: "actions", label: "Actions" },
                  ].map((column) => (
                    <ModernTableCell
                      key={column.id}
                      onClick={() => {
                        if (column.id !== "actions") {
                          let direction = "desc";
                          if (sortConfig.key === column.id) {
                            direction =
                              sortConfig.direction === "desc" ? "asc" : "desc";
                          }
                          setSortConfig({ key: column.id, direction });
                        }
                      }}
                      sx={{
                        cursor: column.id !== "actions" ? "pointer" : "default",
                        "&:hover": {
                          backgroundColor:
                            column.id !== "actions"
                              ? alpha(theme.palette.primary.main, 0.2)
                              : "inherit",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {column.label}
                        {sortConfig.key === column.id && (
                          <Box ml={1} display="flex" flexDirection="column">
                            <span
                              style={{
                                lineHeight: 0.5,
                                color:
                                  sortConfig.direction === "asc"
                                    ? "#fff"
                                    : alpha("#fff", 0.5),
                                fontSize: "0.7rem",
                              }}
                            >
                              ▲
                            </span>
                            <span
                              style={{
                                lineHeight: 0.5,
                                color:
                                  sortConfig.direction === "desc"
                                    ? "#fff"
                                    : alpha("#fff", 0.5),
                                fontSize: "0.7rem",
                              }}
                            >
                              ▼
                            </span>
                          </Box>
                        )}
                      </Box>
                    </ModernTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <ModernTableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading tickets...
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                ) : currentTickets?.length > 0 ? (
                  currentTickets.map((ticket) => (
                    <StyledTableRow key={ticket?.id || Math.random()} hover>
                      <ModernTableCell>{ticket?.id || "N/A"}</ModernTableCell>
                      <ModernTableCell>
                        {highlightMatch(
                          isMobile
                            ? `${ticket?.TicketDescription?.substring(0, 15) || ""}...`
                            : (ticket?.TicketDescription?.length || 0) > 30
                            ? `${ticket?.TicketDescription?.substring(0, 27) || ""}...`
                            : ticket?.TicketDescription || "",
                          searchTerm
                        )}
                      </ModernTableCell>
                      <ModernTableCell>
                        {highlightMatch(ticket?.Sender || "", searchTerm)}
                      </ModernTableCell>
                      <ModernTableCell>
                        {ticket?.categoryName || "N/A"}
                      </ModernTableCell>
                      <ModernTableCell>
                        {ticket?.UserRoleId || "N/A"}
                      </ModernTableCell>
                      <ModernTableCell>
                        <Tooltip title={formatDateTime(ticket?.AssignedOn)}>
                          <Box>
                            <Box
                              fontWeight={
                                sortConfig.key === "AssignedOn" ? 600 : 400
                              }
                            >
                              {ticket?.AssignedOn
                                ? new Date(ticket.AssignedOn).toISOString().split("T")[0]
                                : "N/A"}
                            </Box>
                            <Box fontSize="0.75rem" color="text.secondary">
                              {ticket?.AssignedOn
                                ? new Date(ticket.AssignedOn).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                : "N/A"}
                            </Box>
                          </Box>
                        </Tooltip>
                      </ModernTableCell>
                      <ModernTableCell>
                        {ticket?.assignedFrom || "N/A"}
                      </ModernTableCell>
                      <ModernTableCell>
                        {highlightMatch(
                          isMobile
                            ? `${ticket?.assignReason?.substring(0, 10) || ""}...`
                            : ticket?.assignReason || "",
                          searchTerm
                        )}
                      </ModernTableCell>
                      <ModernTableCell>
                        {ticket?.AssignedTo || "N/A"}
                      </ModernTableCell>
                      <ModernTableCell>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(ticket)}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                              ),
                            },
                          }}
                        >
                          <VisibilityIcon
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        </IconButton>
                      </ModernTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <ModernTableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      No assigned tickets found.
                    </ModernTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={Math.ceil(filteredTickets.length / ticketsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Box>

        {/* Filter Status Display */}
        {selectedCategory ? (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Module: {selectedCategory}
            </Typography>
          </Box>
        ) : null}
      </MainContent>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            [theme.breakpoints.down("sm")]: {
              margin: "16px",
              width: "calc(100% - 32px)",
            },
          },
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.primary.light,
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Ticket Details: {selectedTicket?.Sender}</span>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseDialog}
            size="small"
          >
            <CloseIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold">
                Ticket Description:
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="body1">
                  {selectedTicket?.TicketDescription}
                </Typography>
              </Paper>
              <Typography variant="subtitle1" fontWeight="bold">
                Created Date:
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="body1">
                  {selectedTicket?.CreatedOn
                    ? formatDateTime(selectedTicket.CreatedOn)
                    : "N/A"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                Sender:
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="body1">
                  <strong>Phone:</strong> {selectedTicket?.PhoneNumber}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                Assigned By:
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="body1" fontWeight="bold">
                  {selectedTicket?.assignedFrom}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                User Role:
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="body1">
                  {selectedTicket?.UserRoleId || "Not specified"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                Module:
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="body1">
                  {selectedTicket?.categoryName || "Not specified"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold">
                Assignment Reason:
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="body1">
                  {selectedTicket?.assignReason || "No reason provided"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold">
                Attachment:
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                View Attachment
                {selectedTicket?.AttachmentRefPath ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      href={`https://documents.ippis.rw/preview/${selectedTicket.AttachmentRefPath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="text"
                      color="primary"
                      sx={{ textTransform: "none" }}
                      startIcon={<VisibilityIcon />}
                    ></Button>
                  </Box>
                ) : (
                  <Typography variant="body1">
                    No attachment available
                  </Typography>
                )}
              </Paper>
            </Grid>

            {(user.id === selectedTicket?.personAssignedTo ||
              user.UserRoleId === "ADMIN") && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Your Response:
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Type your response here..."
                  sx={{ mt: 1 }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "center" }}>
          {(user.id === selectedTicket?.personAssignedTo ||
            user.UserRoleId === "ADMIN") && (
            <Button
              onClick={handleRespond}
              color="primary"
              variant="contained"
              disabled={!responseText.trim()}
            >
              Respond
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
      <Footer collapsed={collapsed} />
    </MainContainer>
  );
};

export default AssignedTickets;
